import { Component, OnInit } from '@angular/core';
import { ReportService } from '@proxy/reports';
import { Column } from '../../shared/components/metro-table/metro-table.component';
import { ClientReportDto, GetClientReportDto, ClientOrderDetailDto } from '@proxy/reports/dto';
import { ListService, PagedResultDto } from '@abp/ng.core';
import { APP_ICONS } from '../../shared/icons/app-icons';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Temporal } from '@js-temporal/polyfill';

@Component({
  selector: 'app-clients-report',
  templateUrl: './clients-report.component.html',
  standalone: false,
  providers: [ListService]
})
export class ClientsReportComponent implements OnInit {
  columnsGrouped: Column[] = [
    { name: 'customerName', label: '::Name', width: '20%' },
    { name: 'customerEmail', label: '::Email', width: '20%', textColor: 'text-metro-blue-secondary' },
    { name: 'customerPhone', label: '::Phone', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'totalSpent', label: '::TotalSpent', width: '15%', textColor: 'text-metro-blue-secondary', type: 'price' },
    { name: 'totalOrders', label: '::TotalOrders', width: '10%', textColor: 'text-metro-blue-secondary' },
    { name: 'lastPurchaseDate', label: '::LastPurchaseDate', width: '15%', textColor: 'text-metro-blue-secondary', type: 'date' },
  ];

  columnsUngrouped: Column[] = [
    { name: 'customerName', label: '::Name', width: '18%' },
    { name: 'customerEmail', label: '::Email', width: '18%', textColor: 'text-metro-blue-secondary' },
    { name: 'customerPhone', label: '::Phone', width: '12%', textColor: 'text-metro-blue-secondary' },
    { name: 'lastPurchaseDate', label: '::OrderDate', width: '12%', textColor: 'text-metro-blue-secondary', type: 'date' },
    { name: 'totalSpent', label: '::Total', width: '12%', textColor: 'text-metro-blue-secondary', type: 'price' },
    { name: 'sommelierName', label: '::Sommelier', width: '18%', textColor: 'text-metro-blue-secondary' },
    { name: 'orderStatus', label: '::Status', width: '10%', textColor: 'text-metro-blue-secondary' },
  ];

  get columns(): Column[] {
    return this.isGrouped ? this.columnsGrouped : this.columnsUngrouped;
  }

  data: PagedResultDto<ClientReportDto> = { items: [], totalCount: 0 };
  searchParams: Partial<GetClientReportDto> = {};
  isLoading = false;
  isGrouped = true; // Por defecto vista agrupada

  fromDate: Temporal.PlainDate | null = null;
  toDate: Temporal.PlainDate | null = null;

  expandedRows: Set<string> = new Set();
  Math = Math;

  constructor(
    private reportService: ReportService,
    public readonly list: ListService,
    private sanitizer: DomSanitizer
  ) { }

  get viewModeIcon(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.isGrouped ? APP_ICONS.grouped : APP_ICONS.ungrouped);
  }

  ngOnInit(): void {
    // Cargar automáticamente al iniciar
    this.isLoading = true;
    const streamCreator = (query) => {
      const params: GetClientReportDto = {
        ...query,
        isGrouped: this.isGrouped,
      };
      
      // Solo agregar keyword si tiene valor y no está vacío
      if (this.searchParams.keyword && this.searchParams.keyword.trim()) {
        params.keyword = this.searchParams.keyword.trim();
      }
      
      // Agregar fechas si están disponibles
      if (this.fromDate) {
        params.from = this.fromDate.toString();
      }
      if (this.toDate) {
        params.to = this.toDate.toString();
      }
      
      return this.reportService.getClientReport(params);
    };

    this.list.hookToQuery(streamCreator).subscribe({
      next: (result) => {
        this.data = result;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  toggleViewMode(): void {
    this.isGrouped = !this.isGrouped;
    this.expandedRows.clear(); // Limpiar filas expandidas al cambiar de vista
    this.list.page = 0;
    this.isLoading = true;
    this.list.get();
  }

  onSearch(searchTerm: string) {
    if (searchTerm && searchTerm.trim()) {
      this.searchParams.keyword = searchTerm.trim();
    } else {
      delete this.searchParams.keyword;
    }
    this.searchParams.skipCount = 0;
    this.list.page = 0;
    this.isLoading = true;
    this.list.get();
  }

  onClearSearch() {
    this.searchParams = {} as GetClientReportDto;
    delete this.searchParams.keyword;
    this.list.page = 0;
    this.isLoading = true;
    this.list.get();
  }

  onPageChange(page: number): void {
    this.list.page = page;
    this.isLoading = true;
    this.list.get();
  }

  onFromDateChange(date: Temporal.PlainDate | null | undefined) {
    this.fromDate = date || null;
    this.list.page = 0;
    this.isLoading = true;
    this.list.get();
  }

  onToDateChange(date: Temporal.PlainDate | null | undefined) {
    this.toDate = date || null;
    this.list.page = 0;
    this.isLoading = true;
    this.list.get();
  }

  toggleRowDetails(userId: string | undefined): void {
    if (!userId) return;
    if (this.expandedRows.has(userId)) {
      this.expandedRows.delete(userId);
    } else {
      this.expandedRows.add(userId);
    }
  }

  isRowExpanded(userId: string | undefined): boolean {
    if (!userId) return false;
    return this.expandedRows.has(userId);
  }

  getOrderDetails(userId: string | undefined): ClientOrderDetailDto[] {
    if (!userId) return [];
    const client = this.data.items.find(c => c.userId === userId);
    return client?.orderDetails || [];
  }

  getOrderStatusTranslationKey(status: string | undefined | null): string {
    if (!status) return '::Unknown';
    
    // El estado viene como string del backend (ej: "Paid", "Pending", etc.)
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'pending':
        return '::OrderPending';
      case 'paid':
        return '::Paid';
      case 'shipped':
        return '::Shipped';
      case 'delivered':
        return '::Delivered';
      case 'cancelled':
      case 'canceled':
        return '::Cancelled';
      default:
        return '::Unknown';
    }
  }
}

